import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  ErrorHandler,
  NgModule,
  isDevMode,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { environment } from '@merchant-portal/environments/environment';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { OpenFeatureModule } from '@openfeature/angular-sdk';
import { GoFeatureFlagWebProvider } from '@openfeature/go-feature-flag-web-provider';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { MessageService, SharedModule } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { providePrimeNG } from 'primeng/config';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { ToastModule } from 'primeng/toast';
import MyPreset from 'ui-component/src/lib/theme/preset-theme';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb.component';
import { SvgLoaderModule } from './components/icon/svg-loader/svg-loader/svg-loader.module';
import { LoadingModule } from './components/loading/loading.module';
import { NotificationComponent } from './components/notification/notification.component';
import { CoreModule } from './core/core.module';
import { BackwardHttpInterceptor } from './core/helper/backward-http-interceptor.interceptor';
import { featureFlagInitializerFactory } from './core/initializers/feature-flag.initializer';
import { ErrorHandlerService } from './core/services/error-handler.service';
import { FeatureFlagService } from './core/services/feature-flag.service';
import { ENVIRONMENT_TOKEN } from './core/services/global-token';
import { DashboardModule } from './features/dashboard/dashboard.module';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { ErrorInterceptor } from './interceptors/error.interceptor';
import { HttpHeaderInterceptor } from './interceptors/http-header.interceptor';
import { MerchantAccessInterceptor } from './interceptors/merchant-access.interceptor';
import { StompServiceFactory } from './services/stomp/stomp-factory.service';
import { StompService } from './services/stomp/stomp.service';
import { AppEffects } from './store/app.effects';
import { appReducer } from './store/app.reducers';

@NgModule({
  declarations: [AppComponent],
  imports: [
    BreadcrumbComponent,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    SharedModule,
    ButtonModule,
    CoreModule,
    DashboardModule,
    LoadingModule,
    SvgLoaderModule,
    ToastModule,
    NotificationComponent,
    StoreModule.forRoot({ app: appReducer }), // Ensure your reducer is registered
    EffectsModule.forRoot([AppEffects]),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: !isDevMode(),
      connectInZone: true,
    }),
    OpenFeatureModule.forRoot({
      provider: new GoFeatureFlagWebProvider({
        endpoint: environment.feature_flag_host,
        retryDelayMultiplier: 2,
        maxRetries: 3,
      }),
    }),
    GoogleTagManagerModule.forRoot({
      id: environment.googleTagManager.id,
    }),
  ],
  providers: [
    MessageService,
    DialogService,
    DynamicDialogConfig,
    provideAnimationsAsync(),

    providePrimeNG({
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: '.my-app-dark',
        },
      },
    }),
    {
      provide: ENVIRONMENT_TOKEN,
      useValue: environment,
    },
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpHeaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BackwardHttpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MerchantAccessInterceptor,
      multi: true,
    },
    {
      provide: StompService,
      useFactory: StompServiceFactory,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: featureFlagInitializerFactory,
      deps: [FeatureFlagService],
      multi: true,
    },
    {
      provide: 'googleTagManagerId',
      useValue: environment.googleTagManager.id,
    },
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
